import type { OnboardingTier } from '@/pages/OnboardingHub';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { User, Users, Building2, ArrowRight, Check } from 'lucide-react';

interface Props {
  onSelect: (tier: OnboardingTier) => void;
}

const tiers: {
  id: OnboardingTier;
  title: string;
  subtitle: string;
  icon: typeof User;
  features: string[];
  badge?: string;
}[] = [
  {
    id: 'single_surgeon',
    title: 'Single Surgeon',
    subtitle: 'Solo practice — minimal admin, maximum clinical focus',
    icon: User,
    features: [
      'Surgeon authorizes data access once',
      'AI learns dictation style from 20+ op notes',
      'Pre-built CPT bundles from your specialty',
      'One-click payer rule sync',
    ],
    badge: 'Fastest',
  },
  {
    id: 'small_practice',
    title: 'Small Practice',
    subtitle: '2–10 surgeons — shared admin, per-surgeon customization',
    icon: Users,
    features: [
      'Everything in Single Surgeon',
      'Multi-surgeon profile management',
      'Shared preference cards & templates',
      'Practice-level payer analytics',
      'Staff role assignments',
    ],
    badge: 'Popular',
  },
  {
    id: 'enterprise',
    title: 'Enterprise Hospital',
    subtitle: 'Health system deployment — IT integration, compliance, scale',
    icon: Building2,
    features: [
      'Everything in Small Practice',
      'Epic EHR integration (SMART on FHIR)',
      'Bulk 837 file import & historical analysis',
      'Department-level rule governance',
      'HIPAA audit trail & compliance dashboard',
      'Dedicated implementation team',
    ],
  },
];

export function OnboardingTierSelector({ onSelect }: Props) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {tiers.map((tier) => {
        const Icon = tier.icon;
        return (
          <Card
            key={tier.id}
            className="group relative border-border hover:border-primary/50 transition-all duration-300 cursor-pointer bg-card"
            onClick={() => onSelect(tier.id)}
          >
            {tier.badge && (
              <Badge className="absolute -top-2.5 right-4 bg-primary text-primary-foreground text-[10px] px-2">
                {tier.badge}
              </Badge>
            )}
            <CardHeader className="pb-3">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-3 group-hover:bg-primary/20 transition-colors">
                <Icon className="w-6 h-6 text-primary" />
              </div>
              <CardTitle className="text-lg">{tier.title}</CardTitle>
              <p className="text-sm text-muted-foreground">{tier.subtitle}</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <ul className="space-y-2">
                {tier.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button className="w-full group-hover:bg-primary/90" size="sm">
                Select <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
